import { useState, useCallback } from 'react';

export interface ShareData {
  title: string;
  text?: string;
  url: string;
}

export interface UseShareReturn {
  share: (data: ShareData) => Promise<void>;
  copyToClipboard: (text: string) => Promise<boolean>;
  copied: boolean;
  canUseNativeShare: boolean;
}

/**
 * Hook for sharing content via native share API or clipboard
 */
export function useShare(): UseShareReturn {
  const [copied, setCopied] = useState(false);

  // Check if native share API is available
  const canUseNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return true;
      } catch {
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }, []);

  const share = useCallback(async (data: ShareData): Promise<void> => {
    if (canUseNativeShare) {
      try {
        await navigator.share(data);
      } catch (err) {
        // User cancelled or share failed - fall back to clipboard
        if (err instanceof Error && err.name !== 'AbortError') {
          await copyToClipboard(data.url);
        }
      }
    } else {
      // Fallback to clipboard copy
      await copyToClipboard(data.url);
    }
  }, [canUseNativeShare, copyToClipboard]);

  return {
    share,
    copyToClipboard,
    copied,
    canUseNativeShare,
  };
}

/**
 * Build shareable URL for a topic or episode
 */
export function buildShareUrl(topicSlug: string, episodeId?: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://kaster.media';
  const topicUrl = `${baseUrl}/p/${topicSlug}`;

  if (episodeId) {
    return `${topicUrl}?episode=${encodeURIComponent(episodeId)}`;
  }

  return topicUrl;
}
